import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AvatarIcon from './AvatarIcon';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
  MagnifyingGlass,
  MapPin,
  Phone,
  Clock,
  ShoppingBag,
  CaretRight,
  Pill,
  NavigationArrow,
  Sparkle,
  Drop,
  FirstAid,
  Lightning,
  Heartbeat,
  Virus,
  Thermometer,
  Sun,
  Bed
} from '@phosphor-icons/react';
import { BlogSection, ArticleView } from '../../components/blog/BlogSection';
import GallerySection from '../../components/gallery/GallerySection';
import AgentHonorSection from '../../components/community/AgentHonorSection';
import HeroOfMonthCard from '../../components/pharmacy/HeroOfMonthCard';
import TestimonialsSection from '../../components/community/TestimonialsSection';
import { GoogleReviewButton } from '../../components/reviews/GoogleReviewButton';
import HomePharmacyOnDutyBanner from '../../components/garde/HomePharmacyOnDutyBanner';
import AnniversarySection, { isInAnniversaryWeek } from '../../components/anniversary/AnniversarySection';
import SeoHead from '../../components/seo/SeoHead';
import GoogleReviewPopup from '../../components/GoogleReviewPopup';
import SMSOptInPopup from '../../components/SMSOptInPopup';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Petite bannière "Nous rejoindre" affichée sur la home, avec compteur dynamique
const RecrutementHomeBanner = ({ onNavigate }) => {
  const [openCount, setOpenCount] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/careers/jobs/summary`)
      .then((res) => {
        if (!cancelled) setOpenCount(res.data?.open_count ?? 0);
      })
      .catch(() => {
        if (!cancelled) setOpenCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // On affiche toujours la bannière (même à 0 poste ouvert : les
  // candidatures spontanées restent possibles).
  const label =
    openCount === null
      ? 'CV & lettres de motivation acceptés'
      : openCount === 0
        ? 'Candidatures spontanées bienvenues'
        : `${openCount} poste${openCount > 1 ? 's' : ''} ouvert${openCount > 1 ? 's' : ''} · Candidatures spontanées`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="mt-3"
    >
      <button
        onClick={() => onNavigate('recrutement')}
        className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-white to-[#F5EDE4] border-2 border-[#C9A086]/40 hover:border-[#5D4E37] hover:from-[#F5EDE4] hover:to-[#EDE0D0] rounded-xl transition-all group"
        data-testid="recrutement-home-banner"
      >
        <div className="flex items-center gap-3">
          <AvatarIcon slug="recrutement" size={40} alt="Rejoindre l'équipe SADA" />
          <div className="text-left">
            <span className="font-semibold text-gray-900 block">Nous rejoindre</span>
            <span className="text-xs text-gray-500" data-testid="recrutement-home-banner-count">
              {label}
            </span>
          </div>
        </div>
        <CaretRight size={20} className="text-[#5D4E37] group-hover:translate-x-1 transition-transform" />
      </button>
    </motion.div>
  );
};

const PharmacyHomePage = ({ onNavigate, cartCount = 0 }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      onNavigate('catalog', { search: searchQuery });
    }
  };

  const categories = [
    { id: 'vitamines', name: 'Vitamines', icon: Sparkle, color: 'bg-amber-50 text-amber-600' },
    { id: 'fortifiants', name: 'Fortifiants', icon: Lightning, color: 'bg-rose-50 text-rose-600' },
    { id: 'medicaments', name: 'Médicaments', icon: Pill, color: 'bg-blue-50 text-blue-600' },
    { id: 'cosmetique', name: 'Cosmétique', icon: Drop, color: 'bg-emerald-50 text-emerald-600' },
  ];

  const expertiseAreas = [
    { id: 'diabete', name: 'Diabète', icon: Pill, color: 'bg-blue-50 border-blue-100' },
    { id: 'hta', name: 'Hypertension', icon: Heartbeat, color: 'bg-red-50 border-red-100' },
    { id: 'palu', name: 'Paludisme', icon: Virus, color: 'bg-emerald-50 border-emerald-100' },
    { id: 'grippe', name: 'États grippaux', icon: Thermometer, color: 'bg-amber-50 border-amber-100' },
    { id: 'peau', name: 'Soins de peau', icon: Sun, color: 'bg-pink-50 border-pink-100' },
    { id: 'fatigue', name: 'Fatigue', icon: Bed, color: 'bg-slate-50 border-slate-100' },
  ];

  const pharmacyInfo = {
    name: 'Pharmacie Saint Ange Des Amis',
    tagline: 'Le Compagnon de votre Santé',
    address: 'Angre 7e tranche carrefour Bluetooth, Abidjan',
    phone: '07 78 41 52 77',
    phoneLandline: '27 22 30 05 72',
    mapsUrl: 'https://www.google.com/maps/place/92V7%2BH8+Abidjan',
    hours: { weekdays: '7h30 - 20h00', saturday: '7h30 - 13h00' },
  };

  const logoUrl = 'https://customer-assets.emergentagent.com/job_skincare-check-1/artifacts/6cemh04i_LOGO%20DEF.jpg';

  return (
    <div className="min-h-screen bg-[#F9FAFB] font-body">
      <GoogleReviewPopup />
      <SMSOptInPopup />
      <SeoHead
        title="Pharmacie de référence à Cocody Angré 7ème Tranche — Click & Collect, Parapharmacie, Dermo-cosmétique"
        description="Pharmacie Saint Ange Des Amis à Angré 7ème Tranche, Cocody. Hall aéré et spacieux, équipe jeune et dynamique, accueil chaleureux. Médicaments, parapharmacie fournie, laits infantiles rares, commandes France & Suisse, conseil dermo-cosmétique par IA. Ouvert Lun-Ven 7h30-20h, Sam 7h30-13h."
        canonical="/"
      />
      {/* Header - Compact with Logo & Cart */}
      <header className="bg-white/90 backdrop-blur-xl border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src={logoUrl} 
                alt="Pharmacie SADA" 
                className="h-10 w-auto object-contain"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onNavigate('cart')}
                className="relative text-gray-600 hover:text-[#2E7D32] hover:bg-[#E8F5E9]"
                data-testid="cart-btn"
              >
                <ShoppingBag size={22} weight="duotone" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#2E7D32] text-white text-xs rounded-full flex items-center justify-center font-medium">
                    {cartCount}
                  </span>
                )}
              </Button>
              <Button
                onClick={() => onNavigate('staff-login')}
                variant="outline"
                className="border-[#2E7D32] text-[#2E7D32] hover:bg-[#E8F5E9] rounded-lg px-2.5 sm:px-3 py-2 text-xs sm:text-sm font-medium flex items-center gap-1 sm:gap-1.5 transition-colors whitespace-nowrap"
                data-testid="staff-login-btn"
              >
                <span className="hidden sm:inline">Espace</span> Conseiller
                <CaretRight size={14} weight="bold" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section - Name + Prominent Search */}
      <motion.section 
        className="relative bg-gradient-to-b from-[#E8F5E9] to-white overflow-hidden"
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
      >
        <div className="max-w-4xl mx-auto px-4 py-10 md:py-14 text-center">
          <motion.div variants={fadeInUp}>
            {/* Pharmacy Name - Prominent */}
            <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-[#2E7D32] mb-2">
              {pharmacyInfo.name}
            </h1>
            <p className="text-gray-600 text-base md:text-lg mb-8">
              {pharmacyInfo.tagline}
            </p>

            {/* Search Bar - PROMINENT & CENTERED */}
            <form onSubmit={handleSearch} className="relative max-w-2xl mx-auto">
              <div className="relative shadow-lg rounded-2xl">
                <MagnifyingGlass 
                  size={26} 
                  weight="bold" 
                  className="absolute left-5 top-1/2 transform -translate-y-1/2 text-[#2E7D32]" 
                />
                <Input
                  type="text"
                  placeholder="Rechercher un médicament, vitamine, produit..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-14 pr-40 py-7 text-lg rounded-2xl border-2 border-[#2E7D32]/30 bg-white focus:ring-4 focus:ring-[#2E7D32]/20 focus:border-[#2E7D32] transition-all placeholder:text-gray-400"
                  data-testid="search-input"
                />
                <Button
                  type="submit"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-[#2E7D32] hover:bg-[#1B5E20] rounded-xl px-8 py-3 text-base font-semibold transition-colors shadow-md"
                  data-testid="search-btn"
                >
                  Rechercher
                </Button>
              </div>
            </form>

            {/* Quick Category Pills */}
            <div className="flex flex-wrap justify-center gap-2 mt-6">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => onNavigate('catalog', { category: cat.id })}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-gray-200 rounded-full text-sm font-medium text-gray-700 hover:bg-[#E8F5E9] hover:border-[#2E7D32] hover:text-[#2E7D32] transition-all"
                >
                  <cat.icon size={16} weight="duotone" />
                  {cat.name}
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Découvrez nos services — Teasing SADA */}
      <section className="max-w-7xl mx-auto px-4 py-12 sm:py-16" data-testid="discover-services-section">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <p className="text-xs uppercase tracking-wider font-semibold text-[#2E7D32] mb-2">
            Nos services en pharmacie
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            Une pharmacie qui vous accompagne vraiment
          </h2>
          <p className="text-gray-600 text-base max-w-2xl mx-auto">
            Au-delà de la délivrance de médicaments, nous analysons vos besoins et vous proposons des solutions sur mesure.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            {
              kind: 'skincare',
              image: '/images/teasing_skincare.png',
              accent: '#F5A623',
              accentSoft: '#FEF3E7',
              tag: 'SADASkinCare',
              title: 'Diagnostic peau',
              hook: 'Ne choisissez plus vos produits au hasard',
              testId: 'discover-skincare-card',
              avatarSlug: 'skincare',
            },
            {
              kind: 'health',
              image: '/images/teasing_health.png',
              accent: '#0E7490',
              accentSoft: '#E0F2FE',
              tag: 'SADAHealth',
              title: 'Suivi tension & glycémie',
              hook: 'Quelques minutes, et vous savez où vous en êtes',
              testId: 'discover-health-card',
              avatarSlug: 'health',
            },
            {
              kind: 'bebe',
              image: '/images/teasing_bebe.png',
              accent: '#DB2777',
              accentSoft: '#FCE7F3',
              tag: 'Conseil Bébé',
              title: 'Espace dédié maman',
              hook: "Conseils sur mesure pour l'âge de votre bébé",
              testId: 'discover-bebe-card',
              avatarSlug: 'baby',
            },
          ].map((c) => (
            <motion.button
              key={c.kind}
              onClick={() => onNavigate('teaser', { kind: c.kind })}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              whileHover={{ y: -4 }}
              transition={{ duration: 0.3 }}
              className="text-left bg-white rounded-2xl overflow-hidden shadow-card hover:shadow-card-hover transition-shadow border border-gray-100 group"
              data-testid={c.testId}
            >
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src={c.image}
                  alt={c.tag}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <span
                  className="absolute top-3 left-3 text-xs font-semibold px-3 py-1 rounded-full"
                  style={{ background: c.accent, color: '#fff' }}
                >
                  {c.tag}
                </span>
                {/* Badge avatar SADA (identité visuelle cohérente sur toutes les sections) */}
                <div
                  className="absolute -bottom-6 right-4 rounded-full shadow-lg border-4 border-white overflow-hidden bg-white flex items-center justify-center"
                  style={{ width: 60, height: 60 }}
                >
                  <AvatarIcon
                    slug={c.avatarSlug}
                    size={52}
                    ring={c.accent}
                    bg="#fff"
                    alt={c.title}
                  />
                </div>
              </div>
              <div className="p-5 pt-8">
                <h3 className="font-display text-xl font-bold text-gray-900 mb-1">{c.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{c.hook}</p>
                <span
                  className="inline-flex items-center gap-1 text-sm font-semibold group-hover:gap-2 transition-all"
                  style={{ color: c.accent }}
                >
                  Découvrir →
                </span>
              </div>
            </motion.button>
          ))}
        </div>
      </section>

      {/* Infos Pratiques - Compact Unified Block */}
      <section className="max-w-7xl mx-auto px-4 py-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <Card className="bg-white border border-gray-100 shadow-card rounded-2xl overflow-hidden">
            <CardContent className="p-0">
              <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-100">
                {/* Phone */}
                <a href={`tel:${pharmacyInfo.phone.replace(/\s/g, '')}`} className="flex items-center gap-4 p-5 hover:bg-[#E8F5E9]/50 transition-colors">
                  <div className="w-12 h-12 bg-[#E8F5E9] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone size={24} weight="duotone" className="text-[#2E7D32]" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Appelez-nous</p>
                    <p className="text-gray-900 font-semibold">{pharmacyInfo.phone}</p>
                  </div>
                </a>
                
                {/* Hours */}
                <div className="flex items-center gap-4 p-5">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock size={24} weight="duotone" className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Horaires</p>
                    <p className="text-gray-900 font-medium text-sm">Lun-Ven: {pharmacyInfo.hours.weekdays}</p>
                    <p className="text-gray-500 text-xs">Sam: {pharmacyInfo.hours.saturday}</p>
                  </div>
                </div>
                
                {/* GPS */}
                <a href={pharmacyInfo.mapsUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-5 hover:bg-[#E8F5E9]/50 transition-colors">
                  <div className="w-12 h-12 bg-[#2E7D32] rounded-xl flex items-center justify-center flex-shrink-0">
                    <NavigationArrow size={24} weight="fill" className="text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Itinéraire</p>
                    <p className="text-[#2E7D32] font-semibold text-sm">Ouvrir GPS →</p>
                  </div>
                </a>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </section>

      {/* Pharmacies de Garde - Compact Link */}
      <section className="max-w-7xl mx-auto px-4 pb-4 space-y-3">
        <HomePharmacyOnDutyBanner onNavigate={onNavigate} compact />
        {isInAnniversaryWeek() && <AnniversarySection onNavigate={onNavigate} />}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <button 
            onClick={() => onNavigate('garde')}
            className="w-full flex items-center justify-between p-4 bg-slate-800 hover:bg-slate-700 rounded-xl text-white transition-colors group"
            data-testid="pharmacies-garde-banner"
          >
            <div className="flex items-center gap-3">
              <AvatarIcon slug="garde" size={40} ring="#22C55E" bg="#0F172A" alt="Pharmacie de garde" />
              <span className="font-medium">Pharmacies de Garde</span>
            </div>
            <CaretRight size={20} className="text-white/60 group-hover:translate-x-1 transition-transform" />
          </button>

          {/* Blog link */}
          <button
            onClick={() => onNavigate('blog')}
            className="w-full mt-3 flex items-center justify-between p-4 bg-gradient-to-r from-[#C45C26] to-[#A64C1F] hover:from-[#A64C1F] hover:to-[#8A3E16] rounded-xl text-white transition-colors group"
            data-testid="blog-banner"
          >
            <div className="flex items-center gap-3">
              <AvatarIcon slug="blog" size={40} ring="#FFFFFF" bg="#8A3E16" alt="Blog santé" />
              <div className="text-left">
                <p className="font-medium leading-tight">Blog santé & conseils</p>
                <p className="text-xs text-white/80">Articles de notre pharmacienne</p>
              </div>
            </div>
            <CaretRight size={20} className="text-white/70 group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.div>
      </section>

      {/* Nos domaines d'expertises - Link to Pathologies */}
      <section className="max-w-7xl mx-auto px-4 pb-6 space-y-3">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <button 
            onClick={() => onNavigate('expertises')}
            className="w-full flex items-center justify-between p-4 bg-white border-2 border-[#2E7D32]/20 hover:border-[#2E7D32] hover:bg-[#E8F5E9]/30 rounded-xl transition-all group"
            data-testid="expertises-banner"
          >
            <div className="flex items-center gap-3">
              <AvatarIcon slug="expertises" size={40} ring="#2E7D32" bg="#E8F5E9" alt="Domaines d'expertises" />
              <div className="text-left">
                <span className="font-semibold text-gray-900 block">Nos domaines d&apos;expertises</span>
                <span className="text-xs text-gray-500">Diabète • HTA • Paludisme • Peau • et plus...</span>
              </div>
            </div>
            <CaretRight size={20} className="text-[#2E7D32] group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.div>

        {/* À propos / Notre Histoire */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <button
            onClick={() => onNavigate('about')}
            className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-[#FFF8E7] to-white border-2 border-[#D4AF37]/30 hover:border-[#D4AF37] hover:from-[#FFF3D1] rounded-xl transition-all group"
            data-testid="about-banner"
          >
            <div className="flex items-center gap-3">
              <AvatarIcon slug="histoire" size={40} ring="#D4AF37" bg="#FFF8E7" alt="Notre histoire" />
              <div className="text-left">
                <span className="font-semibold text-gray-900 block">À propos · Notre Histoire</span>
                <span className="text-xs text-gray-500">
                  Depuis 1995 · L&apos;équipe · Valeurs · Partenaires
                </span>
              </div>
            </div>
            <CaretRight size={20} className="text-[#C45C26] group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.div>

        {/* Partenariats */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <button
            onClick={() => onNavigate('partenariats')}
            className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-[#F5EDE4] to-white border-2 border-[#C9A086]/40 hover:border-[#5D4E37] hover:from-[#EDE0D0] rounded-xl transition-all group"
            data-testid="partenariats-banner"
          >
            <div className="flex items-center gap-3">
              <AvatarIcon slug="partenariats" size={40} alt="Partenariats" />
              <div className="text-left">
                <span className="font-semibold text-gray-900 block">Partenariats</span>
                <span className="text-xs text-gray-500">
                  Entreprises · Assurances · ONG · Laboratoires
                </span>
              </div>
            </div>
            <CaretRight size={20} className="text-[#5D4E37] group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.div>

        {/* Nous rejoindre — Recrutement */}
        <RecrutementHomeBanner onNavigate={onNavigate} />
      </section>

      {/* Photothèque - Dynamic Gallery */}
      <GallerySection language="fr" />

      {/* Agent du Mois - À l'honneur */}
      <AgentHonorSection language="fr" />

      {/* Avis Clients */}
      <TestimonialsSection language="fr" />

      {/* Google Review Banner */}
      <section className="py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <GoogleReviewButton variant="banner" showQROption={true} />
        </div>
      </section>

      {/* Héros du mois — reconnaissance équipe */}
      <HeroOfMonthCard />

      {/* Blog Section - Conseils & Actualités */}
      {!selectedArticle ? (
        <BlogSection 
          language="fr" 
          onArticleClick={(article) => setSelectedArticle(article)} 
        />
      ) : (
        <div className="bg-white min-h-screen">
          <ArticleView 
            article={selectedArticle} 
            language="fr" 
            onBack={() => setSelectedArticle(null)} 
          />
        </div>
      )}

      {/* Footer - Compact with Espace Conseiller */}
      <footer className="bg-white border-t border-gray-100 py-8">
        <div className="max-w-7xl mx-auto px-4">
          {/* Main Footer Content */}
          <div className="grid md:grid-cols-3 gap-6 mb-6">
            {/* Logo & Description */}
            <div>
              <div className="flex items-center gap-3 mb-3">
                <img src={logoUrl} alt="SADA" className="h-10 w-auto" />
              </div>
              <p className="font-display font-semibold text-gray-900 text-sm">{pharmacyInfo.name}</p>
              <p className="text-xs text-gray-500">{pharmacyInfo.tagline}</p>
            </div>

            {/* Contact & Hours Combined */}
            <div className="text-sm space-y-2">
              <div className="flex items-start gap-2 text-gray-600">
                <MapPin size={16} className="text-[#2E7D32] mt-0.5 flex-shrink-0" />
                <span className="text-xs">{pharmacyInfo.address}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Phone size={16} className="text-[#2E7D32]" />
                <span className="text-xs">{pharmacyInfo.phone} | {pharmacyInfo.phoneLandline}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Clock size={16} className="text-[#2E7D32]" />
                <span className="text-xs">Lun-Ven: {pharmacyInfo.hours.weekdays} | Sam: {pharmacyInfo.hours.saturday}</span>
              </div>
            </div>

            {/* Social Networks */}
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Suivez-nous</p>
              <div className="flex gap-2">
                <a 
                  href="https://www.facebook.com/p/Pharmacie-Sada-Angrй-100068936199426/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center text-white transition-colors"
                  aria-label="Facebook"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a 
                  href="https://www.instagram.com/pharmsada/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 hover:opacity-90 flex items-center justify-center text-white transition-opacity"
                  aria-label="Instagram"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
                <a 
                  href="https://wa.me/2250778415277"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center text-white transition-colors"
                  aria-label="WhatsApp"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                </a>
                <a 
                  href="https://www.tiktok.com/@pharmacie.saint.a0"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full bg-black hover:bg-gray-800 flex items-center justify-center text-white transition-colors"
                  aria-label="TikTok"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/></svg>
                </a>
                <a
                  href="https://www.linkedin.com/company/pharmacie-saint-ange-des-amis"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full bg-[#0A66C2] hover:bg-[#004182] flex items-center justify-center text-white transition-colors"
                  aria-label="LinkedIn"
                  data-testid="social-linkedin"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.852 3.37-1.852 3.601 0 4.267 2.37 4.267 5.455v6.288zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </a>
              </div>
            </div>
          </div>

          {/* Divider with Espace Conseiller - Discret */}
          <div className="pt-4 border-t border-gray-100 flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-xs text-gray-400">
              © 2025 {pharmacyInfo.name}. Tous droits réservés.
            </p>
            <div className="flex items-center gap-4 flex-wrap">
              <button
                onClick={() => onNavigate('partenariats')}
                className="text-xs text-gray-400 hover:text-[#5D4E37] transition-colors"
                data-testid="footer-link-partenariats"
              >
                Partenariats
              </button>
              <button
                onClick={() => onNavigate('recrutement')}
                className="text-xs text-gray-400 hover:text-[#5D4E37] transition-colors"
                data-testid="footer-link-recrutement"
              >
                Nous rejoindre
              </button>
              <button
                onClick={() => onNavigate('staff-login')}
                className="text-xs text-gray-400 hover:text-[#2E7D32] transition-colors flex items-center gap-1"
                data-testid="espace-conseiller-btn"
              >
                Espace Conseiller
                <CaretRight size={12} />
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PharmacyHomePage;
